import type { PlanningAssessment } from './planningAssessments';

// A pre-application proposal as a conversation: the description a check started from, then each refinement ("make it wooden")
// in order. A refinement's conversation runs back, with no limit, through every earlier finished check until one recorded as a
// new description, which it includes: the same messages planning-assessment-service sends the model.

export type ConversationCheck = Pick<PlanningAssessment, 'id' | 'status' | 'proposalDescription' | 'proposalRefines' | 'createdAt'>;

export interface ConversationMessage { id: string; text: string; refines: boolean; at: string }

export function conversationFor(checks: ConversationCheck[], checkId: string): ConversationMessage[] {
  const ordered = [...checks].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const at = ordered.findIndex(c => c.id === checkId);
  if (at === -1) return [];
  const chain: ConversationCheck[] = [ordered[at]];
  if (ordered[at].proposalRefines) {
    for (let i = at - 1; i >= 0; i--) {
      const c = ordered[i];
      if (c.status !== 'Completed' || !c.proposalDescription) continue;
      chain.push(c);
      // Only a description recorded as new ends the conversation; one from before the flag was kept is taken as part of it.
      if (c.proposalRefines === false) break;
    }
  }
  return chain
    .reverse()
    .filter((c): c is ConversationCheck & { proposalDescription: string } => !!c.proposalDescription)
    // A re-run of the same text adds nothing to the conversation.
    .filter((c, i, all) => i === 0 || c.proposalDescription.trim() !== all[i - 1].proposalDescription.trim())
    .map(c => ({ id: c.id, text: c.proposalDescription, refines: !!c.proposalRefines, at: c.createdAt }));
}
